import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';
import { sendLog } from 'src/components/Logging/sendLog';
import NavItem from './nav-item';
import { NavUl } from '../styles';


export default function NavList({ data, depth, slotProps, user, selectedTenant }) {
  const navRef = useRef(null);
  const pathname = usePathname();
  const active = useActiveLink(data.path, !!data.children);

  const [openMenu, setOpenMenu] = useState(false);

  useEffect(() => {
    if (openMenu) {
      handleCloseMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.children) {
      setOpenMenu(true);
    }
  }, [data.children]);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  const handleNavClick = useCallback(() => {
    const logData = {
      eventType: 'click',
      page: data.title,
      section: 'nav-section',
      component: 'nav-item',
      action: 'click',
      elementType: 'link',
      elementName: data.title,
    };
    
    sendLog(logData, user, user?.uid, user?.displayName, selectedTenant, user?.role);
  }, [data, user ,selectedTenant]);

  return (
    <>
      <NavItem
        ref={navRef}
        open={openMenu}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={handleCloseMenu}
        onClick={handleNavClick}
        title={data.title}
        path={data.path}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        depth={depth}
        hasChild={!!data.children}
        currentRole={slotProps?.currentRole}
        active={active}
        className={active ? 'active' : ''}
        sx={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      />

      {!!data.children && (
        <Popover
          disableScrollLock
          open={openMenu}
          anchorEl={navRef.current}
          anchorOrigin={
            depth === 1
              ? { vertical: 'bottom', horizontal: 'left' }
              : { vertical: 'center', horizontal: 'right' }
          }
          transformOrigin={
            depth === 1
              ? { vertical: 'top', horizontal: 'left' }
              : { vertical: 'center', horizontal: 'left' }
          }
          slotProps={{
            paper: {
              onMouseEnter: handleOpenMenu,
              onMouseLeave: handleCloseMenu,
              sx: {
                minWidth: 160,
                ...(openMenu && {
                  pointerEvents: 'auto',
                }),
              },
            },
          }}
          sx={{
            pointerEvents: 'none',
          }}
        >
          <NavSubList data={data.children} depth={depth} slotProps={slotProps} user={user} selectedTenant={selectedTenant} />
        </Popover>
      )}
    </>
  );
}

NavList.propTypes = {
  data: PropTypes.object,
  depth: PropTypes.number,
  slotProps: PropTypes.object,
  user: PropTypes.object, 
  selectedTenant: PropTypes.string, // Add selectedTenant prop type
};

// function NavSubList({ data, depth, slotProps, user, selectedTenant }) {
//   return (
//     <Stack spacing={0.5}>
//       {data.map((list) => (
//         <NavList key={list.title} data={list} depth={depth + 1} slotProps={slotProps} user={user} selectedTenant={selectedTenant} />
//       ))}
//     </Stack>
//   );
// }
function NavSubList({ data, depth, render, cssVars, slotProps, enabledRootRedirect, user, selectedTenant }) {
  return (
    <NavUl sx={{ gap: 0.5 }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          cssVars={cssVars}
          slotProps={slotProps}
          enabledRootRedirect={enabledRootRedirect}
          user={user}               // Pass down the user prop
          selectedTenant={selectedTenant} // Pass down the selectedTenant prop
        />
      ))}
    </NavUl>
  );
}


NavSubList.propTypes = {
  data: PropTypes.array.isRequired, // Assuming data is required
  depth: PropTypes.number.isRequired, // Assuming depth is required
  render: PropTypes.func, // Adding render prop type
  cssVars: PropTypes.object, // Adding cssVars prop type
  slotProps: PropTypes.object,
  enabledRootRedirect: PropTypes.bool, // Adding enabledRootRedirect prop type
  user: PropTypes.object, // Add user prop type
  selectedTenant: PropTypes.string, // Add selectedTenant prop type
};
